import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Menu, X, ChevronDown, MessageCircle } from 'lucide-react';
import { Button } from './ui/button';
import CountrySelector from './CountrySelector';
import { useCurrency } from '../context/CurrencyContext';

const Navbar = ({ onBookInspection, onViewSampleReport, forceWhite = false, hasBanner = true }) => {
  const { t, selectedCountry } = useCurrency();
  const basePath = `/${selectedCountry.code.toLowerCase()}`;
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navItems = [
    { label: t('nav.products'), href: `${basePath}#services` }, // services acts as products here
    { label: t('nav.howItWorks'), href: `${basePath}#how-it-works` },
    { label: t('nav.technology'), href: `${basePath}#technology` },
    { label: t('nav.aboutUs'), href: `${basePath}#why-us` },
  ];

  // Whether the navbar is currently rendered against a dark hero (transparent
  // mode at the very top of the page) or pinned to a white background after
  // the user scrolls. Drives logo filter + nav-text color so we never get
  // dark-on-dark or light-on-light invisibility.
  const onDarkBg = !forceWhite && !isScrolled;

  return (
    <nav
      className={`fixed left-0 right-0 z-50 transition-all duration-300 ${hasBanner ? 'top-[36px]' : 'top-0'} ${
        forceWhite || isScrolled
          ? 'bg-white/95 backdrop-blur-md shadow-sm'
          : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16 md:h-20">
          {/* Logo — swap between two assets so we get a crisp render at every state.
              White-text logo on the dark hero · proper black-text logo with W mark
              once the navbar pins to the white background. Avoids the brightness-0
              filter hack that was crushing the W's color accent. */}
          <div className="flex-shrink-0">
            <Link to={basePath}>
              <img
                src={onDarkBg ? "/wisedrive-logo-new.png" : "/wisedrive-logo-dark.png"}
                alt="Wisedrive"
                className="h-8 md:h-10 w-auto transition-opacity duration-200"
              />
            </Link>
          </div>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-6">
            {navItems.map((item) => (
              <a
                key={item.label}
                href={item.href}
                className={`font-medium transition-colors duration-200 ${
                  onDarkBg
                    ? 'text-slate-200 hover:text-cyan-300'
                    : 'text-slate-700 hover:text-teal-600'
                }`}
              >
                {item.label}
              </a>
            ))}
            <Link
              to={`${basePath}/business`}
              className={`font-semibold transition-colors duration-200 flex items-center gap-1 ${
                onDarkBg
                  ? 'text-cyan-300 hover:text-cyan-200'
                  : 'text-blue-600 hover:text-blue-700'
              }`}
            >
              {t('nav.forBusinesses')}
            </Link>
            <CountrySelector />
            <a
              href="https://wa.me/917338073380?text=Hi%2C%20I%20want%20to%20know%20more%20about%20Wisedrive%20car%20inspection%20services."
              target="_blank"
              rel="noopener noreferrer"
              data-testid="navbar-whatsapp-us"
              className="inline-flex items-center justify-center gap-1.5 px-5 py-2 bg-green-600 hover:bg-green-700 text-white rounded-full font-semibold text-sm transition-colors shadow-sm"
            >
              <MessageCircle className="h-4 w-4" />
              {t('nav.getStarted')}
            </a>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className={`md:hidden p-2 rounded-lg transition-colors ${
              onDarkBg ? 'hover:bg-white/10' : 'hover:bg-slate-100'
            }`}
          >
            {isMobileMenuOpen ? (
              <X className={`h-6 w-6 ${onDarkBg ? 'text-white' : 'text-slate-900'}`} />
            ) : (
              <Menu className={`h-6 w-6 ${onDarkBg ? 'text-white' : 'text-slate-900'}`} />
            )}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div className="md:hidden bg-white border-t border-slate-200 shadow-lg">
          <div className="px-4 py-4 space-y-3">
            {navItems.map((item) => (
              <a
                key={item.label}
                href={item.href}
                onClick={() => setIsMobileMenuOpen(false)}
                className="block px-4 py-2 text-slate-700 hover:text-blue-600 hover:bg-slate-50 rounded-lg transition-colors"
              >
                {item.label}
              </a>
            ))}
            <Link
              to={`${basePath}/business`}
              onClick={() => setIsMobileMenuOpen(false)}
              className="block px-4 py-2 text-blue-600 font-semibold hover:bg-blue-50 rounded-lg transition-colors"
            >
              {t('nav.forBusinesses')}
            </Link>
            <div className="px-4 py-2">
              <CountrySelector />
            </div>
            <a
              href="https://wa.me/917338073380?text=Hi%2C%20I%20want%20to%20know%20more%20about%20Wisedrive%20car%20inspection%20services."
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => setIsMobileMenuOpen(false)}
              data-testid="mobile-navbar-whatsapp-us"
              className="w-full inline-flex items-center justify-center gap-1.5 bg-green-600 hover:bg-green-700 text-white rounded-full py-2.5 font-semibold text-sm"
            >
              <MessageCircle className="h-4 w-4" />
              {t('nav.getStarted')}
            </a>
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;
